import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface ToolCallStep {
  id: string;
  type: 'search' | 'analyze' | 'process' | 'tool';
  toolName?: string;
  description: string;
  status: 'pending' | 'running' | 'completed';
  args?: Record<string, unknown>;
  result?: unknown;
}

export interface ToolCallDisplayProps {
  thinkingText?: string;
  steps: ToolCallStep[];
  isVisible: boolean;
  defaultExpanded?: boolean;
  className?: string;
}

// Helper to extract screenshot URL from tool result
interface ScreenshotResultData {
  screenshot_url?: string;
  _display_type?: string;
  file_name?: string;
  timestamp_formatted?: string;
}

interface ToolResultWrapper {
  success?: boolean;
  data?: ScreenshotResultData;
}

const getScreenshotFromResult = (
  result: unknown,
): { url: string; caption?: string } | null => {
  if (!result || typeof result !== 'object') return null;

  // Tool results come wrapped as { success: true, data: { ... } }
  const { data } = result as ToolResultWrapper;

  if (data?.screenshot_url && data?._display_type === 'image') {
    const caption = data.file_name
      ? `${data.file_name}${data.timestamp_formatted ? ` @ ${data.timestamp_formatted}` : ''}`
      : undefined;
    return { url: data.screenshot_url, caption };
  }
  return null;
};

/**
 * Minimal tool call display - shows pulsing text and screenshots from completed tool calls
 */
export const ToolCallDisplay: React.FC<ToolCallDisplayProps> = ({
  thinkingText,
  steps,
  isVisible,
  className,
}) => {
  const { t } = useTranslation();

  if (!isVisible) return null;

  const displayText =
    thinkingText || t('chat.toolCall.thinking', 'Thinking...');

  // Check for any screenshots in completed steps
  const screenshots = steps
    .filter((step) => step.status === 'completed' && step.result)
    .map((step) => ({ step, screenshot: getScreenshotFromResult(step.result) }))
    .filter((item) => item.screenshot !== null);

  return (
    <div className={cn('w-full', className)}>
      <span className="text-sm text-muted-foreground animate-pulse">
        {displayText}
      </span>

      {/* Display screenshots from completed tool calls */}
      {screenshots.length > 0 && (
        <div className="mt-3 space-y-2">
          {screenshots.map(({ step, screenshot }) => (
            <div
              key={step.id}
              className="rounded-lg overflow-hidden border border-border bg-muted/30"
            >
              <img
                src={screenshot!.url}
                alt={screenshot!.caption || 'Video screenshot'}
                className="w-full max-w-md h-auto object-contain"
                loading="lazy"
              />
              {screenshot!.caption && (
                <div className="px-2 py-1 text-xs text-muted-foreground bg-muted/50">
                  {screenshot!.caption}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolCallDisplay;
